import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { login as authLogin, logout as authLogout, restoreSession, hasPermission, isSessionValid, type Permission } from "../services/auth";
import type { AuthSession, PortalRole } from "../types";

interface AuthContextValue {
  session: AuthSession | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  can: (permission: Permission) => boolean;
  role: PortalRole | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Restore session on mount
  useEffect(() => {
    const restored = restoreSession();
    if (restored) {
      setSession(restored);
    }
    setLoading(false);
  }, []);

  // Periodically check if session is still valid (user wasn't deleted)
  useEffect(() => {
    if (!session) return;
    const check = async () => {
      try {
        const valid = await isSessionValid(session.username);
        if (!valid) {
          authLogout();
          setSession(null);
          setError("Your account has been removed. Please contact an admin.");
        }
      } catch {
        // network error, don't log out
      }
    };
    const id = setInterval(check, 30000); // check every 30s
    return () => clearInterval(id);
  }, [session]);

  const handleLogin = useCallback(async (username: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const sess = await authLogin(username, password);
      setSession(sess);
    } catch (err: any) {
      const msg = err.message || "Authentication failed";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogout = useCallback(() => {
    authLogout();
    setSession(null);
  }, []);

  const can = useCallback(
    (permission: Permission): boolean => {
      if (!session) return false;
      return hasPermission(session.role, permission);
    },
    [session]
  );

  return (
    <AuthContext.Provider
      value={{
        session,
        loading,
        error,
        login: handleLogin,
        logout: handleLogout,
        can,
        role: session?.role || null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
